import { DateTime } from "luxon";
import React from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Container,
    Divider,
    FormLabel,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { Location, Ticket } from "../model/Types";
import Fetch from "../api/Fetch";
import { BACKEND_URI } from "../constants";
import { DateTimePicker } from "@mui/x-date-pickers";
import SignatureCanvas from "react-signature-canvas";
import Webcam from "react-webcam";
import { CurrencyFormatter, dataURItoBlob } from "../common/Helpers";
import Toaster from "../common/Toaster";
import DeleteIcon from "@mui/icons-material/Delete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SignatureIcon from "@mui/icons-material/Gesture";
import AddIcon from "@mui/icons-material/Add";

const RefundView = () => {
    const canvasRef = React.useRef<SignatureCanvas>(null);
    const webcamRef = React.useRef<Webcam>(null);
    const theme = useTheme();
    const [loading, setLoading] = React.useState<boolean>(false);

    const [request, setRequest] = React.useState<{
        reason: string;
        guestName: string;
        guestNumber: string;
        timeOfSale: DateTime;
        timeOfRefund: DateTime;
        guestSignature: any | null;
        ticketImage: any | null;
        tickets: {
            ticketId: number;
            quantity: number;
        }[];
        locationId: number | null;
    }>({
        reason: "",
        guestName: "",
        guestNumber: "",
        timeOfSale: DateTime.now(),
        timeOfRefund: DateTime.now(),
        guestSignature: null,
        ticketImage: null,
        tickets: [],
        locationId: null,
    });

    const [locations, setLocations] = React.useState<Location[]>([]);
    const [tickets, setTickets] = React.useState<Ticket[]>([]);

    const [ticketRequest, setTicketRequest] = React.useState<{
        ticketId: number;
        quantity: number;
    }>({
        ticketId: 0,
        quantity: 1,
    });

    const onSubmit = async () => {
        setLoading(true);
        try {
            const img = canvasRef.current?.getTrimmedCanvas().toDataURL("image/png");
            const res = await Fetch<string>(`${BACKEND_URI}/refund`, {
                method: "POST",
                includeAuth: true,
                body: {
                    ...request,
                    guestSignature: img,
                },
                isText: true,
            });
            Toaster.success(res);
            setRequest({
                reason: "",
                guestName: "",
                guestNumber: "",
                timeOfSale: DateTime.now(),
                timeOfRefund: DateTime.now(),
                guestSignature: null,
                ticketImage: null,
                tickets: [],
                locationId: null,
            });
            canvasRef.current?.clear();
            webcamRef.current?.video?.play();
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const cb = React.useCallback(async () => {
        try {
            const l = await Fetch<Location[]>(`${BACKEND_URI}/locations`, {
                includeAuth: true,
            });
            setLocations(l.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())));
            setRequest({
                ...request,
                locationId: null,
            });
            const t = await Fetch<Ticket[]>(`${BACKEND_URI}/tickets`, {
                includeAuth: true,
            });
            setTickets(t.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())));
        } catch (e) {
            console.error(e);
        }
    }, []);

    React.useEffect(() => {
        cb();
    }, [cb]);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 500 }}>
                Issue Refund
            </Typography>
            <Paper elevation={2} sx={{ borderRadius: 2 }}>
                <CardHeader
                    title="Refund Request Form"
                    titleTypographyProps={{ variant: "h6" }}
                    avatar={<ReceiptIcon />}
                    sx={{
                        backgroundColor: "primary.main",
                        color: "text.primary",
                    }}
                />
                <Divider />
                <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Gate"
                                select
                                variant="outlined"
                                value={request.locationId || ""}
                                onChange={(e) => {
                                    setRequest({
                                        ...request,
                                        locationId: parseInt(e.target.value, 10),
                                    });
                                }}
                                InputProps={{
                                    startAdornment: <LocationOnIcon sx={{ mr: 1, color: "action.active" }} />,
                                }}
                                required
                            >
                                {locations.map((l) => (
                                    <MenuItem key={l.id} value={l.id}>
                                        {l.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Guest Name"
                                variant="outlined"
                                value={request.guestName}
                                onChange={(e) => {
                                    setRequest({
                                        ...request,
                                        guestName: e.target.value,
                                    });
                                }}
                                InputProps={{
                                    startAdornment: <PersonIcon sx={{ mr: 1, color: "action.active" }} />,
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Order # (last 4)"
                                variant="outlined"
                                value={request.guestNumber}
                                onChange={(e) => {
                                    setRequest({
                                        ...request,
                                        guestNumber: e.target.value,
                                    });
                                }}
                                InputProps={{
                                    startAdornment: <ConfirmationNumberIcon sx={{ mr: 1, color: "action.active" }} />,
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DateTimePicker
                                label="Time of Ticket Sale"
                                value={request.timeOfSale}
                                onChange={(e) => {
                                    setRequest({
                                        ...request,
                                        timeOfSale: e ?? DateTime.now(),
                                    });
                                }}
                                renderInput={(props) => (
                                    <TextField
                                        {...props}
                                        fullWidth
                                        variant="outlined"
                                        InputProps={{
                                            ...props.InputProps,
                                            startAdornment: <AccessTimeIcon sx={{ mr: 1, color: "action.active" }} />,
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DateTimePicker
                                label="Time of Refund"
                                value={request.timeOfRefund}
                                onChange={(e) => {
                                    setRequest({
                                        ...request,
                                        timeOfRefund: e ?? DateTime.now(),
                                    });
                                }}
                                renderInput={(props) => (
                                    <TextField
                                        {...props}
                                        fullWidth
                                        variant="outlined"
                                        InputProps={{
                                            ...props.InputProps,
                                            startAdornment: <AccessTimeIcon sx={{ mr: 1, color: "action.active" }} />,
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Reason for Refund"
                                variant="outlined"
                                value={request.reason}
                                rows={4}
                                multiline
                                onChange={(e) => {
                                    setRequest({
                                        ...request,
                                        reason: e.target.value,
                                    });
                                }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Paper elevation={1} sx={{ borderRadius: 2, overflow: "hidden" }}>
                                <CardHeader
                                    title="Tickets to Refund"
                                    titleTypographyProps={{ variant: "h6" }}
                                    avatar={<ConfirmationNumberIcon />}
                                    sx={{
                                        backgroundColor: "primary.light",
                                        color: "text.primary",
                                    }}
                                />
                                <Divider />
                                <CardContent sx={{ p: 3 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                select
                                                fullWidth
                                                variant="outlined"
                                                label="Select Ticket"
                                                value={ticketRequest.ticketId || ""}
                                                onChange={(e) => {
                                                    setTicketRequest({
                                                        ...ticketRequest,
                                                        ticketId: parseInt(e.target.value, 10),
                                                    });
                                                }}
                                            >
                                                {tickets
                                                    .filter((t) => !request.tickets.find((ti) => ti.ticketId === t.id))
                                                    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                                    .map((t) => (
                                                        <MenuItem value={t.id}>
                                                            {t.name} - {CurrencyFormatter.format(t.price)}
                                                        </MenuItem>
                                                    ))}
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Quantity"
                                                type="number"
                                                variant="outlined"
                                                value={ticketRequest.quantity}
                                                onChange={(e) => {
                                                    setTicketRequest({
                                                        ...ticketRequest,
                                                        quantity: parseInt(e.target.value, 10),
                                                    });
                                                }}
                                                inputProps={{ min: 1 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                size="large"
                                                startIcon={<AddIcon />}
                                                disabled={!ticketRequest.ticketId || !ticketRequest.quantity}
                                                onClick={() => {
                                                    setRequest({
                                                        ...request,
                                                        tickets: [
                                                            ...request.tickets,
                                                            {
                                                                ticketId: ticketRequest.ticketId,
                                                                quantity: ticketRequest.quantity,
                                                            },
                                                        ],
                                                    });
                                                    setTicketRequest({
                                                        ticketId: 0,
                                                        quantity: 1,
                                                    });
                                                }}
                                                sx={{ height: 56, borderRadius: 2 }}
                                            >
                                                Add Ticket
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <TableContainer sx={{ mt: 2 }}>
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: "action.hover" }}>
                                                    <TableCell sx={{ fontWeight: 600 }}>Ticket</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                                                    <TableCell sx={{ fontWeight: 600, width: 100 }}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {request.tickets.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={5} sx={{ textAlign: "center", py: 4 }}>
                                                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                                                No tickets added for refund
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    request.tickets.map((t, index) => {
                                                        const ticket = tickets.find((ti) => ti.id === t.ticketId);
                                                        if (!ticket) {
                                                            return null;
                                                        }
                                                        return (
                                                            <TableRow
                                                                key={ticket.id}
                                                                sx={{
                                                                    "&:hover": { backgroundColor: "action.hover" },
                                                                    backgroundColor:
                                                                        index % 2 === 0 ? "background.paper" : "action.disabledBackground",
                                                                }}
                                                            >
                                                                <TableCell>
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {ticket.name}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>{CurrencyFormatter.format(ticket.price)}</TableCell>
                                                                <TableCell>
                                                                    <Chip label={t.quantity} size="small" variant="outlined" />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body1" fontWeight="medium">
                                                                        {CurrencyFormatter.format(ticket.price * t.quantity)}
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <IconButton
                                                                        color="error"
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setRequest({
                                                                                ...request,
                                                                                tickets: request.tickets.filter((ti) => ti !== t),
                                                                            });
                                                                        }}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })
                                                )}
                                            </TableBody>
                                            <TableFooter>
                                                <TableRow sx={{ backgroundColor: "primary.light" }}>
                                                    <TableCell colSpan={3} sx={{ color: "text.primary", fontWeight: 600 }}>
                                                        Total Refund Amount
                                                    </TableCell>
                                                    <TableCell sx={{ color: "text.primary", fontWeight: 600, fontSize: "1.1rem" }}>
                                                        {CurrencyFormatter.format(
                                                            request.tickets.reduce(
                                                                (acc, t) =>
                                                                    acc + (tickets.find((ti) => ti.id === t.ticketId)?.price ?? 0) * t.quantity,
                                                                0
                                                            )
                                                        )}
                                                    </TableCell>
                                                    <TableCell />
                                                </TableRow>
                                            </TableFooter>
                                        </Table>
                                    </TableContainer>
                                </CardContent>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                    <SignatureIcon color="primary" />
                                    <Typography variant="h6">Guest Signature</Typography>
                                    <Chip label="Required" size="small" color="error" />
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                                    <Box
                                        sx={{
                                            border: 2,
                                            borderColor: "divider",
                                            borderRadius: 1,
                                            backgroundColor: theme.palette.mode === "dark" ? "grey.900" : "grey.50",
                                        }}
                                    >
                                        <SignatureCanvas
                                            canvasProps={{
                                                style: {
                                                    backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50],
                                                },
                                                width: 500,
                                                height: 150,
                                            }}
                                            penColor={theme.palette.mode === "dark" ? "white" : "black"}
                                            ref={canvasRef}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ textAlign: "center" }}>
                                    <Button onClick={() => canvasRef.current?.clear()} variant="outlined" color="error" sx={{ borderRadius: 2 }}>
                                        Clear Signature
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                        {/* <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                            <Box sx={{ display: "flex", flexDirection: "column", maxWidth: "100%", width: 500 }}>
                                <FormLabel>Photo of Tickets</FormLabel>
                                <Webcam
                                    videoConstraints={{
                                        facingMode: "environment",
                                    }}
                                    style={{
                                        maxWidth: 500,
                                        width: "100%",
                                    }}
                                    ref={webcamRef}
                                />
                                {!request.ticketImage && (
                                    <Button
                                        onClick={() => {
                                            webcamRef.current?.video?.pause();
                                            const ss = webcamRef.current?.getScreenshot();
                                            if (ss) {
                                                setRequest({
                                                    ...request,
                                                    ticketImage: ss,
                                                });
                                            }
                                        }}
                                        color="success"
                                    >
                                        Take photo
                                    </Button>
                                )}
                                {request.ticketImage && (
                                    <Button
                                        onClick={() => {
                                            webcamRef.current?.video?.play();
                                            setRequest({
                                                ...request,
                                                ticketImage: null,
                                            });
                                        }}
                                        color="error"
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Box>
                        </Grid> */}
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                color="error"
                                onClick={onSubmit}
                                disabled={
                                    !request.locationId ||
                                    !request.guestName ||
                                    !request.guestNumber ||
                                    !request.timeOfSale ||
                                    !request.timeOfRefund ||
                                    !request.reason ||
                                    request.tickets.length === 0 ||
                                    loading
                                }
                                sx={{
                                    py: 2,
                                    borderRadius: 2,
                                    fontSize: "1.1rem",
                                    fontWeight: 600,
                                }}
                            >
                                {loading ? "Processing..." : "Issue Refund"}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Paper>
        </Container>
    );
};

export default RefundView;
